<script lang="ts">
  import { scale, blur } from "svelte/transition"
  import { smoothScroll } from "$lib/utils/scroll"
  // Components
  import { Icon } from '$lib/components/atoms/Icon';
  import { Grid } from '$lib/components/organisms/Layout';

  let expanded = false
  let galleryEl: HTMLElement
  let galleryWrapperEl: HTMLElement
  let galleryExpandedWrapperEl: HTMLElement
  let targetIndex: number
  let showPrevButton = false
  let showNextButton = false

  $: if (galleryExpandedWrapperEl) {
    const width = galleryExpandedWrapperEl.clientWidth
    const scroll = targetIndex * width
    galleryExpandedWrapperEl.scrollTo({
      left: scroll,
    })

    updateNavButtonsVisibility()
  }

  $: if (galleryWrapperEl) {
    updateNavButtonsVisibility()
  }

  const openGallery = (e: Event) => {
    if (e.target === e.currentTarget) return

    expanded = true

    const target = e.target as HTMLElement
    targetIndex = Array.prototype.slice.call(target.parentElement.children).indexOf(target)
  }

  const closeGallery = () => {
    expanded = false
    showPrevButton = false
    showNextButton = false
    targetIndex = undefined

    updateNavButtonsVisibility()
  }

  const scroll = (direction: "prev" | "next") => {
    const wrapper = expanded && galleryExpandedWrapperEl ? galleryExpandedWrapperEl : galleryWrapperEl
    const width = wrapper.clientWidth
    const correction = direction === "prev" ? -1 : 1
    const scroll = width * correction

    smoothScroll(wrapper, {
      left: scroll,
      duration: 500,
    })

    updateNavButtonsVisibility()
  }

  const updateNavButtonsVisibility = (nextScrollLeft: number = null) => {
    const wrapper = galleryExpandedWrapperEl || galleryWrapperEl
    if (!wrapper) return

    const scrollLeft = nextScrollLeft ?? wrapper.scrollLeft

    showPrevButton = scrollLeft > 0
    showNextButton = scrollLeft < wrapper.scrollWidth - wrapper.clientWidth
  }
</script>

<div class="gallery" class:expanded>
  <Grid class="relative">
    <div class="gallery-scroll-wrapper" on:scroll="{() => updateNavButtonsVisibility()}" bind:this="{galleryWrapperEl}">
      <div class="gallery-scroll-grid" bind:this="{galleryEl}" on:click="{openGallery}">
        <slot />
      </div>
    </div>

    {#if expanded}
      <div
        class="gallery-scroll-wrapper"
        on:scroll="{() => updateNavButtonsVisibility()}"
        bind:this="{galleryExpandedWrapperEl}"
        in:blur="{{ duration: 500 }}"
        out:blur="{{ duration: 500 }}"
      >
        <div class="gallery-scroll-grid-expanded">
          {@html galleryEl.innerHTML}
        </div>
      </div>
    {/if}

    {#if expanded}
      <button class="gallery-close" on:click="{closeGallery}" in:scale out:scale>
        <Icon icon="close" size="small" />
      </button>
    {/if}
    {#if showPrevButton}
      <button class="gallery-nav prev" on:click="{() => scroll('prev')}" in:scale out:scale>
        <Icon icon="arrow-left" size="small" />
      </button>
    {/if}
    {#if showNextButton}
      <button class="gallery-nav next" on:click="{() => scroll('next')}" in:scale out:scale>
        <Icon icon="arrow-right" size="small" />
      </button>
    {/if}
  </Grid>
</div>

<style type="text/scss" lang="scss" scoped>
  .gallery {
  }

  .gallery-scroll-wrapper {
    @apply row-span-full col-span-full;
    @apply overflow-x-auto;
    scroll-snap-type: x mandatory;
    scroll-snap-points-x: repeat(100%);
    scroll-snap-destination: 100% 0%;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      @apply hidden;
    }
  }
  :global(.gallery .gallery-scroll-grid > *) {
    scroll-snap-align: start;
    @apply row-span-1 col-span-1;
    @apply w-full h-full object-cover object-center;
    @apply cursor-pointer;
    @apply transition-all duration-300;

    &:nth-child(3n + 1) {
      @apply row-span-2 col-span-2;
      @apply md:row-span-1 md:col-span-1;
    }

    &:nth-child(7n + 1) {
      @apply md:row-span-2 md:col-span-2;
    }
    &:nth-child(7n + 5),
    &:nth-child(7n + 7) {
      @apply md:row-span-2 md:col-span-1;
    }
  }

  .gallery-scroll-grid,
  .gallery-scroll-grid-expanded {
    --gallery-gap: theme("spacing.4");
    --gallery-cols: 2;
    --gallery-row-size: 150px;
    --gallery-col-size: calc(
      (100% / var(--gallery-cols)) - (var(--gallery-gap) * (var(--gallery-cols) - 1) / var(--gallery-cols))
    );

    @apply grid grid-flow-col;
    gap: var(--gallery-gap);
    grid-template-columns: repeat(var(--gallery-gap), var(--gallery-col-size));
    grid-template-rows: repeat(3, var(--gallery-row-size));
    grid-auto-columns: var(--gallery-col-size);

    @screen md {
      --gallery-gap: theme("spacing.6");
      --gallery-cols: 4;
      --gallery-row-size: 200px;
    }
  }

  .gallery-scroll-grid-expanded {
    --gallery-gap: 0;
    --gallery-col-size: 100%;
    grid-template-rows: repeat(3, var(--gallery-row-size));
    @apply gap-y-4 md:gap-y-6;
  }
  :global(.gallery .gallery-scroll-grid-expanded > *) {
    @apply w-full h-full object-cover object-center;
    grid-area: span 3 / span 1 !important;
    scroll-snap-align: start;
  }

  .gallery-close,
  .gallery-nav {
    @apply absolute;
    @apply transform -translate-y-1/2;
    @apply appearance-none outline-none shadow-none;
    @apply focus:shadow-none focus:ring-2 focus:ring-offset-2 focus:ring-brand;
    @apply w-10 h-10;
    @apply bg-white bg-opacity-50 rounded-full;
    @apply flex items-center justify-center;
    @apply transition duration-300;
    @apply hover:bg-white hover:bg-opacity-100;
  }

  .gallery-close {
    @apply right-8 md:right-16 top-8 md:top-10;
    @apply shadow-sm;
    @apply transform -translate-y-1/2;
  }

  .gallery-nav {
    @apply left-8 md:left-16 top-1/2;
    @apply transform -translate-y-1/2;

    &.next {
      @apply left-auto right-8 md:right-16;
    }
  }
</style>
